import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Closed-set palette shared between the Boxes overlay and the Add Container
 * form. Keep the keys in lock-step with `_ALLOWED_BOX_COLORS` on the backend
 * — the API rejects anything outside this set.
 *
 *   • "clear" is the default — renders as an outlined-only swatch (no fill)
 *     so the user can visually tell "no color chosen" from black.
 */
export const BOX_COLOR_OPTIONS = [
  { key: "clear",  label: "None",   fill: "transparent", border: "#8A93A6" },
  { key: "red",    label: "Red",    fill: "#E63946",     border: "#B6263A" },
  { key: "orange", label: "Orange", fill: "#F08C2C",     border: "#C0681A" },
  { key: "yellow", label: "Yellow", fill: "#F2C94C",     border: "#C8A12E" },
  { key: "green",  label: "Green",  fill: "#4A8A78",     border: "#356D5D" },
  { key: "blue",   label: "Blue",   fill: "#1F5566",     border: "#143542" },
  { key: "indigo", label: "Indigo", fill: "#465ECF",     border: "#2E45B4" },
  { key: "violet", label: "Violet", fill: "#7C3AED",     border: "#5B23B8" },
  { key: "black",  label: "Black",  fill: "#1F2326",     border: "#000000" },
  { key: "white",  label: "White",  fill: "#FFFFFF",     border: "#C9C4B5" },
];

export const boxColorStyle = (key) => {
  const opt = BOX_COLOR_OPTIONS.find((o) => o.key === key) || BOX_COLOR_OPTIONS[0];
  return { backgroundColor: opt.fill, borderColor: opt.border };
};

/**
 * BoxColorSwatch — tiny rounded rectangle. Tap = open a popover with the
 * full palette. Used in the bottom corner of box cards and inline on the
 * Add Container form so users can color-organise their physical boxes.
 *
 * Stateless: parent owns `value` and decides what to do with `onChange`.
 */
export const BoxColorSwatch = ({
  value = "clear",
  onChange,
  size = "sm",          // "sm" (box card) | "md" (Add Container)
  testid = "box-color-swatch",
  disabled = false,
  label,                // optional label rendered to the LEFT of the swatch (md only)
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("touchstart", onDown);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const sw = size === "md" ? "w-7 h-4" : "w-6 h-3";
  // Cell sizing — small rounded squares like the original. The popover is
  // allowed to overflow the underlying box card so we don't need to cram
  // them tightly together; bumping the gap up to 12px gives each color a
  // ~40px effective tap zone (cell + surrounding gap) without bloating the
  // popover's visible footprint.
  const optionPx = size === "md" ? 28 : 26;

  return (
    <div ref={wrapRef} className="relative inline-flex items-center gap-2">
      {label && size === "md" && (
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          e.preventDefault();
          setOpen((o) => !o);
        }}
        data-testid={testid}
        aria-label={`Box color: ${value}`}
        disabled={disabled}
        className={`${sw} rounded-md border-2 shadow-sm disabled:opacity-50`}
        style={boxColorStyle(value)}
      />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.96 }}
            transition={{ duration: 0.14 }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testid}-popover`}
            // Small squared-circle palette. The popover is permitted to
            // overflow the underlying box card so we can keep the cells
            // visually compact while spacing them out with `gap-3` for
            // thumb-friendly hit zones on mobile.
            className="absolute z-50 bottom-full mb-2 right-0 bg-[#FDFBF7] border border-[#E2E0D8] rounded-2xl p-3 shadow-xl grid grid-cols-5 gap-3"
            // Without a fixed width the absolutely-positioned grid was
            // collapsing to ~74px, which squished every cell down to a
            // sliver. 210px gives each 26px cell its full size + 12px
            // gap + 24px padding without bloating the popover.
            style={{ width: 210 }}
          >
            {BOX_COLOR_OPTIONS.map((opt) => {
              const active = opt.key === value;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange?.(opt.key);
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-${opt.key}`}
                  aria-label={opt.label}
                  title={opt.label}
                  className={`rounded-md border-2 transition-transform hover:scale-110 active:scale-95 ${
                    active ? "ring-2 ring-[#277A82] ring-offset-1" : ""
                  }`}
                  style={{
                    width: optionPx,
                    height: optionPx,
                    ...boxColorStyle(opt.key),
                  }}
                />
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BoxColorSwatch;
